import * as angular from 'angular';
import * as _ from 'lodash';

import { WideError } from '../../common';
import { Dashboard, DateRange } from '../../models';
import { DashboardPeriod, ErrorLevel } from '../../models/enum';

import { IDateNavigationService } from '../helpers';

export interface IdashboardErrorService {
    getError(dashboard: Dashboard): WideError;
    getErrorBetweenDates(dashboard, beginDate, endDate): angular.IPromise<WideError>;
    getRange(period: DashboardPeriod): angular.IPromise<DateRange>;
}

export class DashboardErrorService implements IdashboardErrorService {
    static serviceId = 'dashboardErrorService';
    static $inject = ['$q', 'dateNavigationService'];

    constructor(
        private _$q: angular.IQService,
        private _dateNavigationService: IDateNavigationService
    ) {
    }

    getRange(period: DashboardPeriod): angular.IPromise<DateRange> {
        let promise: angular.IPromise<DateRange>;
        switch (period) {
            case DashboardPeriod.Today:
                promise = this._dateNavigationService.getToday();
                break;
            case DashboardPeriod.Yesterday:
                promise = this._dateNavigationService.getYesterday();
                break;
            case DashboardPeriod.Last24H:
                promise = this._dateNavigationService.getLast24h();
                break;
            case DashboardPeriod.LastWeek:
                promise = this._dateNavigationService.getLastWeek();
                break;
            case DashboardPeriod.LastMonth:
                promise = this._dateNavigationService.getLastMonth();
                break;
            case DashboardPeriod.WeekToDate:
                promise = this._dateNavigationService.getWeekToDate();
                break;
            case DashboardPeriod.MonthToDate:
                promise = this._dateNavigationService.getMonthToDate();
                break;
            case DashboardPeriod.NextMonth:
                promise = this._dateNavigationService.getNextMonth();
                break;
            case DashboardPeriod.LastQuarter:
                promise = this._dateNavigationService.getLastQuarter();
                break;
            case DashboardPeriod.Last3Months:
                promise = this._dateNavigationService.getLast3Months();
                break;
            case DashboardPeriod.ThisQuarter:
                promise = this._dateNavigationService.getThisQuarter();
                break;
            case DashboardPeriod.QuarterToDate:
                promise = this._dateNavigationService.getQuarterToDate();
                break;
            case DashboardPeriod.NextQuarter:
                promise = this._dateNavigationService.getNextQuarter();
                break;
            case DashboardPeriod.LastYear:
                promise = this._dateNavigationService.getLastYear();
                break;
            case DashboardPeriod.Last12Months:
                promise = this._dateNavigationService.getLast12Months();
                break;
            case DashboardPeriod.ThisYear:
                promise = this._dateNavigationService.getThisYear();
                break;
            case DashboardPeriod.YearToDate:
                promise = this._dateNavigationService.getYearToDate();
                break;
            case DashboardPeriod.NextYear:
                promise = this._dateNavigationService.getNextYear();
                break;
            default:
                break;
        }
        return promise;
    }

    getErrorBetweenDates(dashboard, beginDate, endDate): angular.IPromise<WideError> {
        const defered = this._$q.defer<WideError>();
        const promises = dashboard.configuration.widgets
            .filter(w => !!w.getErrorBetweenDates)
            .map(w => w.getErrorBetweenDates(beginDate, endDate));
        this._$q.all(promises)
            .then(errorList => {
                defered.resolve(this.getWorstError(errorList as any[], ''));
            })
            .catch((reason) => { defered.reject(reason); });
        return defered.promise;
    }


    getError(dashboard: Dashboard): WideError {
        const errorList = [];

        dashboard.configuration.widgets.forEach((w: any) => {
            if (w.getError) {
                errorList.push(w.getError());
            }
        });

        // add each error message to its collection
        const filterMessages = (level: ErrorLevel) => <string[]>_.chain(errorList)
            .filter((error: WideError) => error !== null && error.level === level)
            .map('message')
            .value();
        const fatalErrorMessages = filterMessages(ErrorLevel.Fatal);
        const errorErrorMessages = filterMessages(ErrorLevel.Error);
        const warningErrorMessages = filterMessages(ErrorLevel.Warning);
        const infoErrorMessages = filterMessages(ErrorLevel.Info);

        // PREPARE HEADER MESSAGE
        const headerMessage = this.getHeaderMessage(fatalErrorMessages.length, errorErrorMessages.length, warningErrorMessages.length, infoErrorMessages.length);
        const errorMessages = fatalErrorMessages.concat(errorErrorMessages).concat(warningErrorMessages).concat(infoErrorMessages);
        const errorsCount = fatalErrorMessages.length + errorErrorMessages.length + warningErrorMessages.length + infoErrorMessages.length;

        // GET CONCATENED MESSAGE
        const concatenedMessage = this.getConcatenedMessage(errorsCount, headerMessage, errorMessages);

        // get worst error
        const worstError = this.getWorstError(errorList, concatenedMessage);
        return worstError;
    }

    private getHeaderMessage(fatalErrorMessagesLength: number, errorErrorMessagesLength: number,
        warningErrorMessagesLength: number, infoErrorMessagesLength: number): string {

        // PREPARE HEADER MESSAGE
        let headerMessage = '';

        // function for concat header
        const concatHeader = (header: string, errorCount: number, errorStr: string) => {
            if (errorCount > 0) {
                header += errorCount + ' ' + errorStr;
                if (errorCount > 1) {
                    header += 's';
                }
                header += ', ';
            }
            return header;
        };

        // concat
        headerMessage = concatHeader(headerMessage, fatalErrorMessagesLength, 'fatal');
        headerMessage = concatHeader(headerMessage, errorErrorMessagesLength, 'error');
        headerMessage = concatHeader(headerMessage, warningErrorMessagesLength, 'warning');
        headerMessage = concatHeader(headerMessage, infoErrorMessagesLength, 'info');

        // remove extra ', ' at the end of line
        if (headerMessage.length > 0) {
            headerMessage = headerMessage.substring(0, headerMessage.length - 2);
        }

        return headerMessage;
    }

    private getConcatenedMessage(errorCount: number, headerMessage: string, errorMessages: string[]): string {

        // GET CONCATENED MESSAGE
        let concatenedMessage = '';
        let bodyMessage = '';
        if (errorCount > 4) {
            bodyMessage = errorMessages.slice(0, 3).join('\n');
            concatenedMessage = headerMessage + '\n\n' + bodyMessage + '\nmore...';
        } else if (errorCount > 1) {
            bodyMessage = errorMessages.join('\n');
            concatenedMessage = headerMessage + '\n\n' + bodyMessage;
        } else if (errorCount === 1) {
            concatenedMessage = errorMessages[0];
        }

        return concatenedMessage;
    }

    private getWorstError(errorList: WideError[], concatenedMessage: string): WideError {
        // set the default error
        let worstError = new WideError(ErrorLevel.Success, 'Unknown');
        try {
            // inline function for condition
            const condition = (level: ErrorLevel): boolean =>
                _.some(errorList, (e: WideError) => e.level === level);
            // inline function to create error    
            const createError = (level: ErrorLevel) =>
                new WideError(level, concatenedMessage);

            // take the worst error
            if (condition(ErrorLevel.Fatal)) {
                worstError = createError(ErrorLevel.Fatal);
                worstError.classed = 'panel-fatal';
            } else if (condition(ErrorLevel.Error)) {
                worstError = createError(ErrorLevel.Error);
                worstError.classed = 'panel-error';
            } else if (condition(ErrorLevel.Warning)) {
                worstError = createError(ErrorLevel.Warning);
                worstError.classed = 'panel-warning';
            } else if (condition(ErrorLevel.Info)) {
                worstError = createError(ErrorLevel.Info);
                worstError.classed = 'panel-info';
            } else if (condition(ErrorLevel.Success)) {
                worstError = createError(ErrorLevel.Success);
            }
        } catch (e) {
            //this.loggerService.logError(e);
        }

        return worstError;
    }
}
